// src/pages/Inscription.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import BoutonGoogle, { Separateur } from "../components/BoutonGoogle";

export default function Inscription() {
  const { inscrire } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    pseudo: "",
    nomAsso: "",
    email: "",
    password: "",
    passwordConfirm: "",
    cgv: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
    setError("");
  }

  function validate() {
    if (!form.nomAsso.trim()) return "Indiquez le nom de votre association ou de votre club.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Email invalide.";
    if (form.password.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
    if (form.password !== form.passwordConfirm) return "Les mots de passe ne correspondent pas.";
    if (!form.cgv) return "Vous devez accepter les CGV pour continuer.";
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      // Nom de l'organisation = nom de l'association saisi (modifiable ensuite dans Mon profil)
      const nomAuto = form.nomAsso.trim().slice(0, 120);
      await inscrire({
        email: form.email.trim(),
        password: form.password,
        nomOrganisation: nomAuto,
        pseudo: form.pseudo.trim(),
        codePromo: "",
      });
      setEmailSent(true);
    } catch (e) {
      if (e.code === "auth/email-already-in-use") setError("Cet email est déjà utilisé. Connectez-vous, ou utilisez « Continuer avec Google » si vous avez créé votre compte avec Google.");
      else if (e.code === "auth/weak-password") setError("Mot de passe trop faible (8 caractères min).");
      else setError("Erreur lors de la création du compte. Réessayez.");
    }
    setLoading(false);
  }

  // ── Email de vérification envoyé ──────────────────────────────────────────
  if (emailSent) {
    return (
      <div style={s.wrapper}>
        <div style={s.card}>
          <div style={s.successIcon}>📧</div>
          <h1 style={s.title}>Vérifiez votre email !</h1>
          <p style={s.subtitle}>
            Un email de confirmation a été envoyé à <strong>{form.email}</strong>.
            Cliquez sur le lien pour activer votre compte.
          </p>
          <div style={s.infoBox}>
            <p style={s.infoText}>
              💡 Vérifiez vos spams si vous ne recevez pas l'email dans 5 minutes.
            </p>
          </div>
          <button onClick={() => navigate("/connexion")} style={s.btnPrimary}>
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.wrapper}>
      <div style={s.card}>
        {/* Header */}
        <div style={s.header}>
          <a href="https://tombo.live" style={s.logoLink}>
            <img src="/logo_app.png" alt="TomboLive" style={s.logoImg} />
          </a>
          <h1 style={s.title}>Créer votre compte</h1>
          <p style={s.subtitle}>Gratuit jusqu'à 100 billets · Sans carte bancaire</p>
        </div>

        {/* Tarifs */}
        <div style={s.planSelector}>
          <div style={s.planCard}>
            <div style={s.planName}>Starter</div>
            <div style={s.planPrice}>Gratuit</div>
            <div style={s.planDesc}>100 billets max par tombola</div>
          </div>
          <div style={s.planArrow}>→</div>
          <div style={{ ...s.planCard, ...s.planCardPro }}>
            <div style={s.planBadge}>⭐ Pro</div>
            <div style={s.planPrice}>29€ <span style={s.planPer}>/tombola</span></div>
            <div style={s.planDesc}>Billets illimités + exports</div>
          </div>
        </div>

        {/* Inscription en un clic : le nom de l'association est demandé juste après */}
        <BoutonGoogle
          label="S'inscrire avec Google"
          onError={setError}
          onUser={() => {
            try { if (form.nomAsso.trim()) sessionStorage.setItem("tl_nomAsso", form.nomAsso.trim().slice(0, 120)); } catch { /* rien */ }
            navigate("/");
          }}
        />
        <Separateur texte="ou avec votre email" />

        {/* Formulaire */}
        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>👤 Votre prénom</label>
            <input type="text" placeholder="Ex : Arnaud"
              value={form.pseudo}
              onChange={(e) => setField("pseudo", e.target.value)}
              style={s.input} autoFocus />
          </div>

          <div style={s.field}>
            <label style={s.label}>🏢 Nom de l'association ou du club *</label>
            <input type="text" placeholder="Ex : Comité des fêtes de Desnes"
              value={form.nomAsso}
              onChange={(e) => setField("nomAsso", e.target.value)}
              maxLength={120} style={s.input} required />
          </div>

          <div style={s.field}>
            <label style={s.label}>✉️ Email *</label>
            <input type="email" placeholder="votre@email.com"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              style={s.input} required />
          </div>

          <div style={s.field}>
            <label style={s.label}>🔒 Mot de passe * <span style={s.labelHint}>(8 caractères min)</span></label>
            <div style={s.passwordRow}>
              <input type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                style={{ ...s.input, flex: 1, marginBottom: 0 }} required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={s.eyeBtn}>
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>🔒 Confirmer le mot de passe *</label>
            <input type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={form.passwordConfirm}
              onChange={(e) => setField("passwordConfirm", e.target.value)}
              style={s.input} required />
          </div>

          {/* CGV */}
          <label style={s.cgvLabel}>
            <input type="checkbox" checked={form.cgv}
              onChange={(e) => setField("cgv", e.target.checked)}
              style={{ marginRight: "8px" }} />
            J'accepte les{" "}
            <a href="https://tombo.live/cgv" target="_blank" rel="noopener noreferrer" style={s.cgvLink}>
              Conditions Générales de Vente
            </a>
            {" "}et la{" "}
            <a href="https://tombo.live/confidentialite" target="_blank" rel="noopener noreferrer" style={s.cgvLink}>
              Politique de confidentialité
            </a>
          </label>

          {error && <div style={s.errBox}>⚠️ {error}</div>}

          <button type="submit" disabled={loading} style={s.btnPrimary}>
            {loading ? "Création du compte…" : "🚀 Créer mon compte gratuitement"}
          </button>
        </form>

        <p style={s.loginLink}>
          Déjà un compte ?{" "}
          <Link to="/connexion" style={s.link}>Se connecter</Link>
        </p>
      </div>
    </div>
  );
}

const s = {
  wrapper: { minHeight:"100vh", background:"linear-gradient(135deg,#1a3a8f,#764ba2)", display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"2rem 1rem", paddingTop:"3rem" },
  card: { background:"#fff", borderRadius:"20px", width:"100%", maxWidth:"420px", padding:"2rem", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  header: { textAlign:"center", marginBottom:"1.5rem" },
  logoLink: { display:"inline-flex", alignItems:"center", justifyContent:"center", textDecoration:"none", marginBottom:"0.5rem" },
  logoImg: { width:"120px", height:"120px", objectFit:"contain" },
  title: { fontSize:"1.5rem", fontWeight:"800", color:"#1a1a2e", marginBottom:"0.25rem" },
  subtitle: { fontSize:"0.85rem", color:"#888" },
  successIcon: { fontSize:"3rem", textAlign:"center", marginBottom:"1rem" },
  infoBox: { background:"#eef0ff", borderRadius:"10px", padding:"0.85rem 1rem", marginBottom:"1.25rem" },
  infoText: { margin:0, fontSize:"0.82rem", color:"#4c5ee8" },
  // Plan selector
  planSelector: { display:"flex", alignItems:"center", gap:"8px", marginBottom:"1.5rem", background:"#f7f8fc", borderRadius:"12px", padding:"0.75rem" },
  planCard: { flex:1, textAlign:"center", padding:"0.5rem" },
  planCardPro: { background:"linear-gradient(135deg,#eef0ff,#f5f0ff)", borderRadius:"8px", position:"relative" },
  planBadge: { fontSize:"0.7rem", fontWeight:"700", color:"#764ba2", marginBottom:"2px" },
  planName: { fontWeight:"700", color:"#1a1a2e", fontSize:"0.85rem" },
  planPrice: { fontSize:"1rem", fontWeight:"900", color:"#667eea" },
  planPer: { fontSize:"0.68rem", fontWeight:"400", color:"#888" },
  planDesc: { fontSize:"0.7rem", color:"#888", marginTop:"2px" },
  planArrow: { fontSize:"1.25rem", color:"#c7d2ff", flexShrink:0 },
  // Form
  form: { display:"flex", flexDirection:"column", gap:"1rem" },
  field: { display:"flex", flexDirection:"column", gap:"6px" },
  label: { fontSize:"0.85rem", fontWeight:"600", color:"#444" },
  labelHint: { fontWeight:"400", color:"#aaa", fontSize:"0.75rem" },
  input: { padding:"0.7rem 1rem", border:"1.5px solid #e0e0e0", borderRadius:"10px", fontSize:"0.95rem", outline:"none", width:"100%", boxSizing:"border-box" },
  passwordRow: { display:"flex", gap:"8px", alignItems:"center" },
  eyeBtn: { background:"#f5f5f5", border:"1.5px solid #e0e0e0", borderRadius:"8px", padding:"0.65rem 0.75rem", cursor:"pointer", fontSize:"1rem", flexShrink:0 },
  cgvLabel: { display:"flex", alignItems:"flex-start", gap:"4px", fontSize:"0.82rem", color:"#555", lineHeight:1.5, cursor:"pointer" },
  cgvLink: { color:"#667eea", textDecoration:"none", fontWeight:"600" },
  errBox: { background:"#ffebee", color:"#b71c1c", borderRadius:"10px", padding:"0.75rem 1rem", fontSize:"0.85rem" },
  btnPrimary: { padding:"0.9rem", background:"linear-gradient(135deg,#667eea,#764ba2)", color:"#fff", border:"none", borderRadius:"12px", fontSize:"1rem", fontWeight:"800", cursor:"pointer", boxShadow:"0 4px 20px rgba(102,126,234,0.4)" },
  loginLink: { textAlign:"center", marginTop:"1rem", fontSize:"0.85rem", color:"#888" },
  link: { color:"#667eea", textDecoration:"none", fontWeight:"600" },
};
